"use client"

import { motion, AnimatePresence } from "framer-motion"
import { Code, Smartphone, ShoppingCart, Zap, Palette, Globe, Cpu, Bot, ArrowRight } from "lucide-react"
import { Section } from "@/components/layout/Section"
import Link from "next/link"
import { useState } from "react"
import { CodeAnimation } from "@/components/ui/CodeAnimation"
import { getTranslations } from "@/lib/translations"
import type { Locale } from "@/lib/i18n"

const iconMap: Record<string, any> = {
  "Web Tasarım": Palette,
  "Web Design": Palette,
  "Web Yazılım": Code,
  "Web Development": Code,
  "Mobil Uygulama": Smartphone,
  "Mobile App": Smartphone,
  "E-ticaret Sistemleri": ShoppingCart,
  "E-Commerce Systems": ShoppingCart,
  "Otomasyon": Zap,
  "Automation": Zap,
  "AI Destekli Servisler": Bot,
  "AI-Powered Services": Bot,
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
}

type ServicesProps = {
  locale: Locale
}

export function Services({ locale }: ServicesProps) {
  const t = getTranslations(locale)
  const services = t.services.items.map(item => ({
    ...item,
    icon: iconMap[item.title] || Palette,
  }))
  return (
    <Section className="relative bg-white py-24 lg:py-32 overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white" suppressHydrationWarning />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" suppressHydrationWarning />
      
      {/* Header */}
      <div className="relative z-10 mb-12 sm:mb-16 md:mb-20" suppressHydrationWarning>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 sm:gap-8" suppressHydrationWarning>
          <div className="flex-1" suppressHydrationWarning>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block mb-3 sm:mb-4"
            >
              <span className="text-xs sm:text-sm font-medium text-[rgb(3,74,166)] uppercase tracking-wider">
                {t.services.badge}
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-slate-900 mb-4 sm:mb-6 leading-tight font-mono"
            >
              {t.services.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-base sm:text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed"
            >
              {t.services.description}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="relative z-10" suppressHydrationWarning>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 gap-4 sm:gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-3"
          suppressHydrationWarning
        >
          {services.map((service, index) => {
            const Icon = service.icon
            const [isHovered, setIsHovered] = useState(false)
            
            return (
              <motion.div key={service.title} variants={itemVariants} suppressHydrationWarning>
                <Link 
                  href={service.href}
                  className="group block h-full"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <div className="relative h-full p-4 sm:p-6 md:p-8 border border-slate-200 bg-white hover:border-slate-300 hover:shadow-xl transition-all duration-300" suppressHydrationWarning>
                    {/* Icon / Code Animation Row */}
                    <div className="mb-4 sm:mb-6 min-h-[50px] sm:min-h-[60px] flex items-start" suppressHydrationWarning>
                      <AnimatePresence mode="wait">
                        {!isHovered ? (
                          <motion.div
                            key="icon"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="inline-flex p-3 sm:p-4 bg-slate-100 group-hover:bg-[rgb(3,74,166)] transition-colors duration-300"
                            suppressHydrationWarning
                          >
                            <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700 group-hover:text-white transition-colors duration-300" />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="code"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="w-full"
                            suppressHydrationWarning
                          >
                            <CodeAnimation code={service.code} isVisible={isHovered} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 sm:mb-4 group-hover:text-[rgb(3,74,166)] transition-colors duration-300">
                      {service.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4 sm:mb-6">
                      {service.description}
                    </p>
                    
                    {/* Link */}
                    <div className="flex items-center text-[rgb(3,74,166)] font-medium mt-auto pt-3 sm:pt-4 border-t border-slate-100 group-hover:border-slate-200 transition-colors" suppressHydrationWarning>
                      <span className="text-xs sm:text-sm">{t.services.moreInfo}</span>
                      <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            )
          })}
        </motion.div>
      </div>

      {/* View All Button - Bottom */}
      <div className="relative z-10 flex justify-end items-center gap-2 text-slate-400 mt-8 sm:mt-12" suppressHydrationWarning>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          suppressHydrationWarning
        >
          <Link href={`/${locale}/hizmetler`} className="flex items-center gap-2 group hover:text-slate-600 transition-colors">
            <span className="text-xs sm:text-sm font-medium">{t.services.viewAll}</span>
            <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </Section>
  )
}

